<template>
    <div>
        <div class="dx-field">
            <div v-if="hasLabel" class="dx-field-label">
                {{ title }}
                <div v-if="isRequiered" class="dx-field-item-required-mark">&nbsp;*</div>
            </div>
            <div :class="fieldValueClass">
                <ai-text-box-dictionary
                    ref="aiTextBoxDictionaryRef"
                    :callbacks="callbacks"
                    :custom-buttons="customButtons"
                    :disabled="true"
                    :error="error"
                    :placeholder="placeholder"
                    :title="title"
                    :value="displayValue"
                    @notify-error-message="notifyErrorMessage"
                    @popup-shown="emit('popupShown')"
                    @popup-hidden="emit('popupHidden')"
                    @popup-showing="emit('popupShowing')"
                >
                    <template v-for="(_, name) in $slots" #[name]="{ data }">
                        <slot :data="data" :name="name" locate-in-menu="auto" />
                    </template>
                </ai-text-box-dictionary>
            </div>
        </div>
    </div>
</template>

<script>
export default {
    name: 'ai-form-text-box-dictionary',
};
</script>

<script setup>
import { computed, inject, onMounted, ref } from 'vue';
import AiTextBoxDictionary from '@/components/ai-text-box-dictionary.vue';

const emit = defineEmits(['textBoxRealValueChanged', 'popupShown', 'popupHidden', 'popupShowing']);

const props = defineProps({
    fieldNameText: {
        type: String,
        required: true,
    },
    fieldNameId: {
        type: String,
        required: true,
    },
    title: {
        type: String,
    },
    placeholder: {
        type: String,
    },
    hasLabel: {
        type: Boolean,
        default: true,
    },
    disabled: {
        type: Boolean,
        default: false,
    },
    dataSource: {
        type: Array,
        default: () => [],
    },
    valueExpr: {
        type: String,
        required: true,
    },
    displayExpr: {
        type: [String, Array],
        required: true,
    },
    customButtons: {
        type: Array,
        default: () => [],
    },
    validationRules: {
        type: Object,
        default: () => {},
    },
    callbacks: {
        type: Object,
        default: () => ({
            dotsClick: () => {
                console.log('aiFormTextBoxDictionary, dotsClick');
            },
        }),
    },
    needModifiedClass: {
        type: Boolean,
        default: true,
    },
});

const serverData = inject('serverData');
const userData = inject('userData');
const validationResult = inject('validationResult');

const isRequiered = computed(() => {
    return props.validationRules?.required;
});

const error = computed(() => {
    return validationResult.value.find(s => s.fieldNameText === props.fieldNameId);
});
const notifyErrorMessage = () => {
    props.callbacks.notifyErrorMessage(error.value.message);
};
const aiTextBoxDictionaryRef = ref(null);
const popupInstance = ref(false);

onMounted(() => {
    popupInstance.value = aiTextBoxDictionaryRef.value?.popupInstance;
});

const realValue = computed(() => {
    const newValue = userData?.value[props.fieldNameId];

    emit('textBoxRealValueChanged', newValue);
    return newValue;
});

const displayValue = computed(() => {
    const item = props.dataSource.find(item => item[props.valueExpr] === realValue.value);
    if (item) {
        if (typeof props.displayExpr === 'string') {
            return item[props.displayExpr];
        }
        if (typeof props.displayExpr === 'object') {
            return props.displayExpr.map(key => item[key]).join(' ');
        }
    }
    return '';
});

const isModified = computed(() => {
    return userData?.value[props.fieldNameId] !== serverData?.value[props.fieldNameId];
});

const fieldValueClass = computed(() => {
    return {
        'dx-field-value': true,
        'ai-field-modified': props.needModifiedClass && isModified.value,
    };
});

const changePopupVisibility = value => {
    aiTextBoxDictionaryRef.value?.changePopupVisibility(value);
};

defineExpose({
    popupInstance,
    changePopupVisibility,
});
</script>
